import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Phone, MapPin, CreditCard, ShieldCheck, Banknote } from "lucide-react";
import { toast } from "sonner";
import { http, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import PriceDetails from "@/components/PriceDetails";
import { AddressForm, validateAddress, loadSaved, rememberCustomer } from "@/components/AddressForm";

const Step = ({ n, title, active, done, children }) => (
  <div className="bg-white rounded-sm fk-shadow mb-3" data-testid={`step-${n}`}>
    <div className={`flex items-center gap-3 px-4 py-3 ${active ? "bg-[#C62828] text-white" : ""}`}>
      <span className={`w-6 h-6 rounded-sm flex items-center justify-center text-xs font-bold ${active ? "bg-white text-[#C62828]" : "bg-[#f0f0f0] text-[#878787]"}`}>
        {done ? <CheckCircle2 className="w-4 h-4 text-[#388e3c]" /> : n}
      </span>
      <span className={`font-head font-semibold ${active ? "text-white" : "text-[#878787]"}`}>{title}</span>
    </div>
    {children}
  </div>
);

const Checkout = () => {
  const navigate = useNavigate();
  const { items, totalMrp, totalPrice, discount, delivery, payable, totalQty, clearCart } = useCart();
  const [step, setStep] = useState(1);

  const [phone, setPhone] = useState(() => loadSaved().phone);
  const [addr, setAddr] = useState(() => loadSaved().addr);
  const [pinStatus, setPinStatus] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState({ has_keys: false, demo_mode: true });
  const [payMethod, setPayMethod] = useState("online");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);

  const codFee = Math.round(totalPrice * 0.12);
  const codTotal = totalPrice + delivery + codFee;

  useEffect(() => {
    http.get("/payment/razorpay/config")
      .then((r) => setPaymentConfig(r.data))
      .catch(() => {});

    if (!document.getElementById("razorpay-checkout-script")) {
      const script = document.createElement("script");
      script.id = "razorpay-checkout-script";
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  if (items.length === 0) {
    return (
      <main className="max-w-[700px] mx-auto px-4 py-16 text-center text-[#878787]">
        Your cart is empty. <a href="/" className="text-[#C62828] font-semibold">Go shopping</a>
      </main>
    );
  }

  // Step 1 — phone only, no OTP
  const savePhone = () => {
    const clean = phone.replace(/\D/g, "");
    if (clean.length < 10) return toast.error("Enter a valid 10-digit mobile number");
    setAddr((a) => ({ ...a, phone: a.phone || clean }));
    rememberCustomer({ phone: clean });
    http.post("/contacts/capture", { phone: clean }).catch(() => {});
    setStep(2);
  };

  const saveAddress = () => {
    const err = validateAddress(addr, pinStatus);
    if (err) return toast.error(err);
    rememberCustomer({ addr });
    http.post("/contacts/capture", {
      phone: addr.phone.replace(/\D/g, ""),
      name: addr.name,
      address: addr,
    }).catch(() => {});
    setStep(3);
  };

  const payNow = async () => {
    setPlacing(true);
    try {
      const orderPayload = {
        phone: addr.phone.replace(/\D/g, ""),
        items: items.map((i) => ({
          id: i.id, name: i.name, price: i.price, mrp: i.mrp, qty: i.qty, image_url: i.image_url,
        })),
        address: addr,
      };

      // 1. Create Razorpay order
      const { data: rzOrder } = await http.post("/payment/razorpay/create-order", orderPayload);

      // 2. Live keys + SDK → open Razorpay modal
      if (!rzOrder.demo_mode && window.Razorpay) {
        const options = {
          key: rzOrder.key_id,
          amount: rzOrder.amount,
          currency: rzOrder.currency || "INR",
          name: "BharatiCrafts",
          description: `Order Payment (${totalQty} items)`,
          order_id: rzOrder.razorpay_order_id,
          prefill: {
            name: addr.name,
            contact: addr.phone.replace(/\D/g, ""),
          },
          theme: { color: "#C62828" },
          handler: async (response) => {
            try {
              const { data: placedOrder } = await http.post("/payment/razorpay/verify-and-place", {
                ...orderPayload,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              clearCart();
              navigate(`/order/${placedOrder.id}`);
            } catch (err) {
              toast.error(err.response?.data?.detail || "Payment verification failed");
              setPlacing(false);
            }
          },
          modal: {
            ondismiss: () => {
              http.post("/payment/razorpay/failed", {
                razorpay_order_id: rzOrder.razorpay_order_id,
                reason: "cancelled",
              }).catch(() => {});
              setPlacing(false);
              toast.info("Payment cancelled");
            },
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", (resp) => {
          http.post("/payment/razorpay/failed", {
            razorpay_order_id: rzOrder.razorpay_order_id,
            reason: resp.error?.description || "Payment failed",
            payment_id: resp.error?.metadata?.payment_id || "",
          }).catch(() => {});
          toast.error(resp.error?.description || "Payment failed");
          setPlacing(false);
        });
        rzp.open();
      } else {
        // 3. Demo / simulated mode: complete payment directly
        const { data: placedOrder } = await http.post("/payment/razorpay/verify-and-place", {
          ...orderPayload,
          razorpay_order_id: rzOrder.razorpay_order_id,
          razorpay_payment_id: "pay_demo_" + Math.random().toString(36).substring(2, 10),
          razorpay_signature: "sig_demo",
        });
        clearCart();
        navigate(`/order/${placedOrder.id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment initiation failed");
      setPlacing(false);
    }
  };

  const sendCodOtp = async () => {
    setSendingOtp(true);
    try {
      await http.post("/checkout/cod/send-otp", {
        phone: addr.phone,
        items: items.map((i) => ({ id: i.id, name: i.name, price: i.price, mrp: i.mrp, qty: i.qty, image_url: i.image_url })),
      });
      setOtpSent(true);
      toast.success("OTP sent to +91 " + addr.phone.replace(/\D/g, "").slice(-10));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send OTP");
    } finally {
      setSendingOtp(false);
    }
  };

  const placeCodOrder = async () => {
    setPlacing(true);
    try {
      await http.post("/checkout/cod/verify-otp", { phone: addr.phone, otp });
      const { data: placedOrder } = await http.post("/orders/cod", {
        phone: addr.phone.replace(/\D/g, ""),
        items: items.map((i) => ({
          id: i.id, name: i.name, price: i.price, mrp: i.mrp, qty: i.qty, image_url: i.image_url,
        })),
        address: addr,
      });
      clearCart();
      navigate(`/order/${placedOrder.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "COD order failed");
      setPlacing(false);
    }
  };

  return (
    <main className="max-w-[1400px] mx-auto px-2 sm:px-4 py-3" data-testid="checkout-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
        <div className="lg:col-span-8">
          {/* Step 1 — Phone number (no OTP) */}
          <Step n={1} title="Mobile Number" active={step === 1} done={step > 1}>
            {step === 1 && (
              <div className="p-4 space-y-4">
                <p className="text-sm text-[#555]">
                  No account needed — just your mobile number for order updates.
                </p>
                <div className="flex gap-2 max-w-md">
                  <div className="flex items-center gap-2 border border-[#c2c2c2] rounded-sm px-3 h-10 text-sm text-[#555] bg-[#f7f7f7]">
                    <Phone className="w-4 h-4" /> +91
                  </div>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="10-digit mobile number"
                    inputMode="numeric"
                    className="flex-1 h-10 border border-[#c2c2c2] rounded-sm px-3 text-sm outline-none focus:border-[#C62828]"
                    data-testid="phone-input"
                  />
                  <button
                    onClick={savePhone}
                    className="bg-[#fb641b] text-white font-semibold px-6 rounded-sm whitespace-nowrap"
                    data-testid="continue-phone-btn"
                  >
                    CONTINUE
                  </button>
                </div>
              </div>
            )}
            {step > 1 && (
              <p className="px-4 py-2 text-sm text-[#555]">
                <CheckCircle2 className="w-4 h-4 text-[#388e3c] inline mr-1" />
                +91 {addr.phone}
              </p>
            )}
          </Step>

          {/* Step 2 — Address with pincode autofill */}
          <Step n={2} title="Delivery Address" active={step === 2} done={step > 2}>
            {step === 2 && (
              <AddressForm addr={addr} setAddr={setAddr} pinStatus={pinStatus} setPinStatus={setPinStatus}>
                <button
                  onClick={saveAddress}
                  className="bg-[#fb641b] text-white font-semibold px-8 h-10 rounded-sm mt-1"
                  data-testid="save-address-btn"
                >
                  SAVE & DELIVER HERE
                </button>
              </AddressForm>
            )}
            {step > 2 && (
              <p className="px-4 py-2 text-sm text-[#555]">
                <MapPin className="w-4 h-4 text-[#C62828] inline mr-1" />
                {addr.name}, {addr.address}, {addr.city}, {addr.state} - {addr.pincode}
              </p>
            )}
          </Step>

          {/* Step 3 — Payment */}
          <Step n={3} title="Payment" active={step === 3}>
            {step === 3 && (
              <div className="p-4 space-y-4" data-testid="payment-section">
                <button
                  type="button"
                  onClick={() => setPayMethod("online")}
                  className={`w-full text-left flex items-center gap-3 border rounded-sm p-3 transition-colors ${
                    payMethod === "online" ? "border-[#C62828] bg-[#FCEBEA]" : "border-[#e0e0e0] bg-white hover:border-[#E5A49F]"
                  }`}
                  data-testid="payment-method-online"
                >
                  <CreditCard className="w-5 h-5 text-[#C62828] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#212121]">
                      Online Payment (UPI, Cards, Netbanking, Wallets)
                    </p>
                    <p className="text-xs text-[#878787]" data-testid="payment-mode-label">
                      {paymentConfig.has_keys
                        ? paymentConfig.key_id?.startsWith("rzp_live")
                          ? "Secure Live Payment · 100% Safe · No extra charges"
                          : `Test Mode (${paymentConfig.key_id})`
                        : "Test / Simulation mode"}
                    </p>
                  </div>
                  {payMethod === "online" && <CheckCircle2 className="w-5 h-5 text-[#C62828] shrink-0" />}
                </button>

                <button
                  type="button"
                  onClick={() => setPayMethod("cod")}
                  className={`w-full text-left flex items-center gap-3 border rounded-sm p-3 transition-colors ${
                    payMethod === "cod" ? "border-[#C62828] bg-[#FCEBEA]" : "border-[#e0e0e0] bg-white hover:border-[#E5A49F]"
                  }`}
                  data-testid="payment-method-cod"
                >
                  <Banknote className="w-5 h-5 text-[#388e3c] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#212121]">Cash on Delivery (COD)</p>
                    <p className="text-xs text-[#878787]">
                      +12% COD fee ({formatPrice(codFee)}) · SMS OTP verification required
                    </p>
                  </div>
                  {payMethod === "cod" && <CheckCircle2 className="w-5 h-5 text-[#C62828] shrink-0" />}
                </button>

                {payMethod === "online" ? (
                  <button
                    onClick={payNow}
                    disabled={placing}
                    className="bg-[#fb641b] text-white font-semibold px-10 h-12 rounded-sm disabled:opacity-60 flex items-center justify-center gap-2"
                    data-testid="pay-now-btn"
                  >
                    <ShieldCheck className="w-5 h-5" />
                    {placing ? "Processing…" : `PAY ${formatPrice(payable)} VIA RAZORPAY`}
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className="border border-[#ffe0b2] bg-[#fff8e1] rounded-sm p-3 text-sm text-[#212121]" data-testid="cod-summary">
                      Item total <b>{formatPrice(totalPrice)}</b> + COD fee (12%) <b>{formatPrice(codFee)}</b>
                      <p className="font-semibold text-[#e65100] mt-1">
                        Pay on delivery: {formatPrice(codTotal)}
                      </p>
                    </div>
                    {!otpSent ? (
                      <button
                        onClick={sendCodOtp}
                        disabled={sendingOtp}
                        className="bg-[#C62828] text-white font-semibold px-8 h-12 rounded-sm disabled:opacity-60"
                        data-testid="cod-send-otp-btn"
                      >
                        {sendingOtp ? "Sending OTP…" : `SEND OTP TO +91 ${addr.phone.replace(/\D/g, "").slice(-10)}`}
                      </button>
                    ) : (
                      <>
                        <div className="flex gap-2 max-w-md">
                          <input
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder="Enter SMS OTP"
                            inputMode="numeric"
                            className="flex-1 h-12 border border-[#c2c2c2] rounded-sm px-3 text-sm outline-none focus:border-[#C62828] tracking-widest"
                            data-testid="cod-otp-input"
                          />
                          <button
                            onClick={sendCodOtp}
                            disabled={sendingOtp}
                            className="text-[#C62828] text-sm font-semibold px-3 disabled:opacity-50"
                            data-testid="cod-resend-otp-btn"
                          >
                            {sendingOtp ? "Sending…" : "RESEND"}
                          </button>
                        </div>
                        <button
                          onClick={placeCodOrder}
                          disabled={placing || otp.length < 4}
                          className="bg-[#fb641b] text-white font-semibold px-10 h-12 rounded-sm disabled:opacity-60 flex items-center justify-center gap-2"
                          data-testid="cod-place-order-btn"
                        >
                          <ShieldCheck className="w-5 h-5" />
                          {placing ? "Placing Order…" : `VERIFY & PLACE COD ORDER · ${formatPrice(codTotal)}`}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </Step>
        </div>

        <div className="lg:col-span-4 lg:sticky lg:top-32">
          <PriceDetails
            totalMrp={totalMrp}
            discount={discount}
            delivery={delivery}
            payable={payable}
            totalQty={totalQty}
            codFee={payMethod === "cod" ? codFee : 0}
          />
        </div>
      </div>
    </main>
  );
};

export default Checkout;
